/** @fileoverview Renders v3 lists with shared server-side filter, sort, count, and page state. */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, Filter } from 'lucide-react';
import { cn } from 'cn';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Popover, PopoverContent, PopoverTitle, PopoverTrigger } from '../ui/popover';
import { Field, FieldLabel } from '../ui/field';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Skeleton } from '../ui/skeleton';
import type {
  FabrikListColumn,
  FabrikListDataSource,
  FabrikListJoin,
  FabrikListRuntimeRow,
  FabrikODataDataSource,
  FabrikListFilter,
  FabrikRenderedListRow,
  FabrikRowValues,
  InputSnapshot,
  FabrikSystemDataSource,
  FabrikWorkdayDataSource,
} from '../../types/apps';
import { toODataCount, toODataRows } from '../../services/fabrikApi';
import { DEFAULT_LIST_PAGE_SIZE } from '../../constants';
import { stableStringify } from '../../lib/utils';
import { jsonPointerSegment } from '../../lib/previewSelection';
import ActionButton from './ActionButton';
import { useComponentStateStore } from './componentStateStore';
import { appButtonLook } from './appButton';
import InlineAlert from './InlineAlert';
import {
  useComponentState,
  useDataReader,
  useDataVersion,
  useFabrikGlobals,
  useLabelOf,
} from './appRuntimeContext';
import { openServerLink } from './openServerLink';
import type { LabelOf } from './fieldOptions';
import { COLUMN_COMPUTES, computeColumnValue, isComputedColumn } from './columnComputes';
import { resolveEffectiveDates } from './effectiveDates';
import { resolveWorkdaySource } from './workdaySource';
import { filterKeyOf, useListFilterValues } from './listFilterStore';
import { resolveODataRowFilters } from './odataRowFilters';
import { hasRequiredODataFieldType } from './globals';
import { formatCellValue, formatRelativeDays, safeExternalUrl, valueForReference } from './helpers';
import { odataLiteral } from './odataLiterals';
import type { FieldValues, ListRendererProps } from './types';

const MAX_ODATA_QUERY_URL_LENGTH = 8192;

/**
 * Tells whether a column shows numbers, so its header and cells line up on the right.
 * @param column the list column
 */
const isNumberColumn = (column: FabrikListColumn): boolean =>
  column.kind === 'person' || column.kind === 'status'
    ? false
    : isComputedColumn(column)
    ? COLUMN_COMPUTES[column.compute]?.outputType === 'number'
    : column.type === 'number';

/**
 * Builds the display text of one cell, and tells whether it is a number.
 * @param column the list column
 * @param row the composed row values, keyed by datasource id
 */
const cellContent = (
  column: FabrikListColumn,
  row: FabrikRowValues,
  globals: ReturnType<typeof useFabrikGlobals>,
  labelOf: LabelOf
): { text: string; isNumber: boolean; tone?: 'past' | 'today' } => {
  if (column.kind === 'person' || column.kind === 'status') return { text: '', isNumber: false };
  if (!isComputedColumn(column)) {
    const raw = valueForReference(row, column.value, globals);
    const { value: ref } = column;
    /* A picklist or foundation object value shows its label; dates and numbers stay as they are. */
    const label =
      column.type === 'text' && ref.scope === 'dataSource'
        ? labelOf(ref.dataSourceId, ref.field, raw)
        : undefined;
    return {
      text: label ?? formatCellValue(raw, column.type),
      isNumber: isNumberColumn(column),
    };
  }
  const value = computeColumnValue({ column, row, globals });
  /* Days until a date read as words, with past days greyed so upcoming ones stand out. */
  if (column.compute === 'daysUntil' && typeof value === 'number') {
    return {
      text: formatRelativeDays(value),
      isNumber: true,
      tone: value < 0 ? 'past' : value === 0 ? 'today' : undefined,
    };
  }
  /* Days since a date read as words too, so a future date shows "In 77 days", not -77. */
  if (column.compute === 'daysSince' && typeof value === 'number') {
    return { text: formatRelativeDays(-value), isNumber: true };
  }
  return {
    text: String(value ?? ''),
    isNumber: isNumberColumn(column),
  };
};

/**
 * Renders the cells of one row.
 * @param columns the list's columns
 * @param row the composed row values, keyed by datasource id
 */
const renderCells = (
  columns: FabrikListColumn[],
  row: FabrikRowValues,
  globals: ReturnType<typeof useFabrikGlobals>,
  labelOf: LabelOf,
  selectionPath?: string,
  rowPath?: string
) =>
  columns.map((column, columnIndex) => {
    const baseClass = cn(
      'h-app-row px-3 py-2 type-body first:pl-4 last:pr-4',
      columnIndex === 0 ? 'font-medium' : 'text-muted-foreground'
    );
    const cellPath = rowPath ? `${rowPath}/cells/${columnIndex}` : undefined;
    const definitionPath = selectionPath ? `${selectionPath}/columns/${columnIndex}` : undefined;
    const selectionProps = {
      'data-fabrik-selection-path': cellPath,
      'data-fabrik-selection-definition-path': definitionPath,
    };
    if (column.kind === 'person') {
      const primary = formatCellValue(valueForReference(row, column.primary, globals), 'text');
      const secondary = column.secondary
        ? formatCellValue(valueForReference(row, column.secondary, globals), 'text')
        : '';
      const initials =
        primary
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((part) => part[0]?.toUpperCase())
          .join('') || '?';
      return (
        <TableCell {...selectionProps} className={baseClass} key={column.name}>
          <div className="flex min-w-0 items-center gap-2.5">
            <span
              className="grid size-8 shrink-0 place-items-center rounded-full bg-muted text-xs font-semibold text-muted-foreground"
              aria-hidden="true"
            >
              {initials}
            </span>
            <span className="min-w-0">
              <span className="block truncate text-card-foreground">{primary || '—'}</span>
              {secondary && (
                <span className="block truncate type-caption text-muted-foreground">
                  {secondary}
                </span>
              )}
            </span>
          </div>
        </TableCell>
      );
    }
    if (column.kind === 'status') {
      const raw = valueForReference(row, column.value, globals);
      const option = column.options.find((entry) => entry.value === raw);
      const tone = option?.tone ?? 'neutral';
      const toneClass =
        tone === 'positive'
          ? 'border-success/30 bg-success/10 text-success'
          : tone === 'warning'
          ? 'border-app-warning-border bg-app-warning-surface text-app-warning'
          : tone === 'negative'
          ? 'border-destructive/30 bg-destructive/10 text-destructive'
          : '';
      return (
        <TableCell {...selectionProps} className={baseClass} key={column.name}>
          <Badge variant="outline" className={toneClass}>
            {(option?.label ?? formatCellValue(raw, 'text')) || '—'}
          </Badge>
        </TableCell>
      );
    }
    const { text, isNumber, tone } = cellContent(column, row, globals, labelOf);
    return (
      <TableCell
        {...selectionProps}
        className={cn(
          baseClass,
          isNumber && 'text-right tabular-nums',
          tone === 'past' && 'text-muted-foreground/70',
          tone === 'today' && 'font-semibold text-foreground'
        )}
        key={column.name}
      >
        {text}
      </TableCell>
    );
  });

/**
 * Text that names a row for screen readers: the first column's value when it is a field column.
 * @param columns the list's columns
 * @param row the composed row values, keyed by datasource id
 * @param fallback text used when the first column is computed or empty
 */
const rowLabel = (
  columns: FabrikListColumn[],
  row: FabrikRowValues,
  fallback: string,
  globals: ReturnType<typeof useFabrikGlobals>
): string => {
  const [titleColumn] = columns;
  const value =
    !titleColumn || isComputedColumn(titleColumn)
      ? undefined
      : titleColumn.kind === 'person'
      ? valueForReference(row, titleColumn.primary, globals)
      : titleColumn.kind === 'status'
      ? valueForReference(row, titleColumn.value, globals)
      : valueForReference(row, titleColumn.value, globals);
  return String(value ?? fallback);
};

const rowKey = (
  componentId: string,
  row: FabrikRenderedListRow,
  primaryId: string,
  index: number
) => {
  const id = row.key ?? row.values[primaryId]?.id ?? row.values[primaryId]?.ID;
  return `${componentId}:${typeof id === 'string' || typeof id === 'number' ? id : index}`;
};

export const querySource = (
  source: FabrikODataDataSource,
  fields: FieldValues,
  globals: ReturnType<typeof useFabrikGlobals>,
  inputs: InputSnapshot,
  additionalFilters: string[] = [],
  orderBy = source.orderBy
): FabrikODataDataSource => {
  const readRef = (ref: Parameters<typeof valueForReference>[1]) =>
    valueForReference(fields, ref, globals, inputs);
  const rowFilters = resolveODataRowFilters(source.rowFilters, fields, globals, inputs);
  const filter = [source.filter, ...rowFilters, ...additionalFilters]
    .filter(Boolean)
    .map((part) => `(${part})`)
    .join(' and ');
  /* Only this source's own effective dates apply to its query. */
  const effectiveDates = resolveEffectiveDates(source.effectiveDates, readRef);
  return {
    ...source,
    orderBy,
    ...(filter ? { filter } : { filter: undefined }),
    effectiveDates,
  };
};

/**
 * Builds the read of a list's primary or metric source: an OData source with its filters and
 * sort, or a Workday source with its references turned into fixed values (Workday reads take no
 * filter or sort).
 */
export const readSourceQuery = (
  source: FabrikSystemDataSource,
  fields: FieldValues,
  globals: ReturnType<typeof useFabrikGlobals>,
  inputs: InputSnapshot,
  additionalFilters: string[] = [],
  orderBy?: FabrikODataDataSource['orderBy']
): FabrikSystemDataSource =>
  source.type === 'workday'
    ? resolveWorkdaySource(source, (ref) => valueForReference(fields, ref, globals, inputs))
    : querySource(source, fields, globals, inputs, additionalFilters, orderBy ?? source.orderBy);

const buildJoinFilter = (
  field: string,
  values: unknown[],
  fieldType?: FabrikListJoin['fieldType']
) => values.map((value) => `${field} eq ${odataLiteral(value, fieldType)}`).join(' or ');

const getODataQueryUrlLength = (source: FabrikODataDataSource, top: number) => {
  const params = new URLSearchParams();
  if (source.values?.length) params.set('$select', source.values.join(','));
  if (source.expand?.length) params.set('$expand', source.expand.join(','));
  if (source.filter !== undefined) params.set('$filter', source.filter);
  if (source.orderBy?.length) {
    params.set(
      '$orderby',
      source.orderBy.map(({ field, direction }) => `${field} ${direction ?? 'asc'}`).join(',')
    );
  }
  params.set('$top', String(top));
  params.set('$skip', '0');
  const query = params.toString().replace(/%24(select|expand|filter|orderby|top|skip)=/g, '$$$1=');
  return `${source.entity}?${query}`.length;
};

const buildJoinFilterBatches = (
  source: FabrikODataDataSource,
  fields: FieldValues,
  globals: ReturnType<typeof useFabrikGlobals>,
  inputs: InputSnapshot,
  joinField: string,
  fieldType: FabrikListJoin['fieldType'],
  values: unknown[],
  top: number
) => {
  const filters: string[] = [];
  let batch: unknown[] = [];
  for (const value of values) {
    const candidate = [...batch, value];
    const candidateSource = querySource(source, fields, globals, inputs, [
      buildJoinFilter(joinField, candidate, fieldType),
    ]);
    if (getODataQueryUrlLength(candidateSource, top) <= MAX_ODATA_QUERY_URL_LENGTH) {
      batch = candidate;
      continue;
    }
    if (!batch.length) throw new Error('A join query exceeds the OData URL limit.');
    filters.push(buildJoinFilter(joinField, batch, fieldType));
    batch = [value];
    const singleSource = querySource(source, fields, globals, inputs, [
      buildJoinFilter(joinField, batch, fieldType),
    ]);
    if (getODataQueryUrlLength(singleSource, top) > MAX_ODATA_QUERY_URL_LENGTH) {
      throw new Error('A join query exceeds the OData URL limit.');
    }
  }
  if (batch.length) filters.push(buildJoinFilter(joinField, batch, fieldType));
  return filters;
};

const composeRows = (
  rows: FabrikRenderedListRow[],
  source: FabrikListDataSource,
  join: FabrikListJoin,
  matches: FabrikListRuntimeRow[],
  globals: ReturnType<typeof useFabrikGlobals>
): FabrikRenderedListRow[] => {
  const byKey = new Map<unknown, FabrikListRuntimeRow[]>();
  for (const match of matches) {
    const key = match[join.field];
    if (key === undefined || key === null) continue;
    const values = byKey.get(key);
    if (values) values.push(match);
    else byKey.set(key, [match]);
  }

  return rows.map((row) => {
    const onValue = valueForReference(row.values, join.on, globals);
    const matchesForValue = byKey.get(onValue) ?? [];
    if (matchesForValue.length > 1) {
      throw new Error(`Datasource ${source.id} has duplicate join values.`);
    }
    return {
      ...row,
      values: { ...row.values, [source.id]: matchesForValue[0] ?? null },
    };
  });
};

/* Placeholder bar widths, varied per cell so the rows read as text, not a grid of boxes. */
const LOADING_BARS = [
  <Skeleton key="wide" className="h-3.5 w-3/4" />,
  <Skeleton key="half" className="h-3.5 w-1/2" />,
  <Skeleton key="most" className="h-3.5 w-2/3" />,
  <Skeleton key="short" className="h-3.5 w-2/5" />,
];
const LOADING_ROW_COUNT = 3;

/**
 * Shows grey placeholder rows shaped like the table while its data loads or waits its turn.
 * @param props how many columns each row spans
 */
const LoadingRows = ({ columnCount }: { columnCount: number }) => (
  <>
    {Array.from({ length: LOADING_ROW_COUNT }, (_, rowIndex) => (
      <TableRow key={rowIndex} className="hover:bg-transparent" aria-hidden="true">
        {Array.from({ length: columnCount }, (_, columnIndex) => (
          <TableCell key={columnIndex} className="h-11 px-3 first:pl-4 last:pr-4">
            {LOADING_BARS[(rowIndex + columnIndex) % LOADING_BARS.length]}
          </TableCell>
        ))}
      </TableRow>
    ))}
    <TableRow className="sr-only">
      <TableCell colSpan={columnCount} role="status">
        Loading data…
      </TableCell>
    </TableRow>
  </>
);

export default function ListRenderer({
  component,
  screenComponents,
  screenId,
  fields,
  inputs,
  onOpenTarget,
  selectedItemId,
  selectionPath,
}: ListRendererProps) {
  const readData = useDataReader();
  const labelOf = useLabelOf();
  const dataVersion = useDataVersion();
  const globals = useFabrikGlobals();
  const [rows, setRows] = useState<FabrikRenderedListRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [pageInput, setPageInput] = useState('1');
  const [countState, setCountState] = useState<{
    queryKey: string;
    count: number | null;
    loaded: boolean;
  }>({
    queryKey: '',
    count: null,
    loaded: false,
  });
  const [pageState, setPageState] = useState({ queryKey: '', page: 1 });
  /* Kept per screen, not in this list, so charts that read this list follow its chips. */
  const {
    values: filterValues,
    setFilterValue,
    clearList: clearFilterValues,
  } = useListFilterValues(component.id);
  const [sortState, setSortState] = useState({ componentId: '', index: 0 });
  const [filterOpen, setFilterOpen] = useState(false);
  /* Raised by "Try again" so the rows load once more with the same query. */
  const [retryCount, setRetryCount] = useState(0);

  const pageSize = component.limit ?? DEFAULT_LIST_PAGE_SIZE;
  const dataSourcesKey = useMemo(
    () => stableStringify(Array.isArray(component.dataSource) ? component.dataSource : []),
    [component.dataSource]
  );
  const dataSources = useMemo(
    () => JSON.parse(dataSourcesKey) as FabrikListDataSource[],
    [dataSourcesKey]
  );
  const primarySource = dataSources.find((source) => source.id === component.primaryDataSourceId);
  const selectedSortIndex =
    sortState.componentId === component.id && component.sortChoices?.[sortState.index]
      ? sortState.index
      : 0;
  const activeSort = component.sortChoices?.[selectedSortIndex];
  const staticPrimaryItems = useMemo(
    () => (primarySource?.type === 'items' ? primarySource.items : []),
    [primarySource]
  );
  const staticRows = useMemo(() => {
    const matching = staticPrimaryItems.filter((item) =>
      (component.filters ?? []).every((filter, index) => {
        const selected = filterValues[filterKeyOf(filter, index)];
        return selected === undefined || item.values[filter.field] === selected;
      })
    );
    if (!activeSort) return matching;
    return [...matching].sort((left, right) => {
      for (const order of activeSort.orderBy) {
        const a = left.values[order.field];
        const b = right.values[order.field];
        const comparison =
          typeof a === 'number' && typeof b === 'number'
            ? a - b
            : String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true });
        if (comparison) return order.direction === 'desc' ? -comparison : comparison;
      }
      return 0;
    });
  }, [activeSort, component.filters, filterValues, staticPrimaryItems]);
  const configuredFilters = useMemo(
    () =>
      (component.filters ?? []).flatMap((filter, index) => {
        const value = filterValues[filterKeyOf(filter, index)];
        return value === undefined
          ? []
          : [`${filter.field} eq ${odataLiteral(value, filter.fieldType)}`];
      }),
    [component.filters, filterValues]
  );
  const selectedOrderBy =
    activeSort?.orderBy ?? (primarySource?.type === 'odata' ? primarySource.orderBy : undefined);
  const fieldsKey = useMemo(() => stableStringify(fields), [fields]);
  const inputsKey = useMemo(() => stableStringify(inputs), [inputs]);
  const queryInputs = useMemo(() => JSON.parse(inputsKey) as InputSnapshot, [inputsKey]);
  const globalsKey = useMemo(() => stableStringify(globals), [globals]);
  const queryFields = useMemo(() => JSON.parse(fieldsKey) as FieldValues, [fieldsKey]);
  const queryKey = useMemo(
    () =>
      stableStringify({
        dataSourcesKey,
        primary: component.primaryDataSourceId,
        pageSize,
        fieldsKey,
        inputsKey,
        globalsKey,
        filterValues,
        sortIndex: selectedSortIndex,
      }),
    [
      dataSourcesKey,
      component.primaryDataSourceId,
      pageSize,
      fieldsKey,
      inputsKey,
      globalsKey,
      filterValues,
      selectedSortIndex,
    ]
  );
  const page = pageState.queryKey === queryKey ? pageState.page : 1;
  const setPage = useCallback((next: number) => setPageState({ queryKey, page: next }), [queryKey]);
  /* The total is read again after a write, which can add or remove records. */
  const countKey = `${queryKey}#${dataVersion}`;
  const countLoaded = countState.queryKey === countKey && countState.loaded;
  const knownCount = countLoaded ? countState.count : null;
  const totalPages = knownCount === null ? null : Math.max(1, Math.ceil(knownCount / pageSize));
  const pagingRef = useRef({ knownCount, totalPages });
  const loadedVersionRef = useRef(dataVersion);
  pagingRef.current = { knownCount, totalPages };

  const commitPageInput = () => {
    const requested = Number(pageInput);
    if (
      Number.isInteger(requested) &&
      requested >= 1 &&
      (totalPages === null || requested <= totalPages)
    ) {
      setPage(requested);
      setPageInput(String(requested));
      return;
    }
    setPageInput(String(page));
  };

  useEffect(() => setPageInput(String(page)), [page]);
  useEffect(() => {
    if (totalPages !== null && page > totalPages) setPage(totalPages);
  }, [page, setPage, totalPages]);

  useEffect(() => {
    if (!primarySource || primarySource.type === 'items' || countLoaded) return;
    const controller = new AbortController();
    void Promise.resolve()
      .then(() =>
        readData({
          dataSource: readSourceQuery(
            primarySource,
            queryFields,
            globals,
            queryInputs,
            configuredFilters,
            selectedOrderBy
          ),
          options: { count: true },
          signal: controller.signal,
        })
      )
      .then((response) => {
        if (!controller.signal.aborted)
          setCountState({ queryKey: countKey, count: toODataCount(response), loaded: true });
      })
      .catch(() => {
        if (!controller.signal.aborted)
          setCountState({ queryKey: countKey, count: null, loaded: true });
      });
    return () => controller.abort();
  }, [
    countLoaded,
    countKey,
    configuredFilters,
    globals,
    primarySource,
    queryFields,
    queryInputs,
    readData,
    selectedOrderBy,
  ]);

  useEffect(() => {
    const primary = dataSources.find((source) => source.id === component.primaryDataSourceId);
    if (!primary) {
      setRows([]);
      setIsLoading(false);
      setError('The primary datasource is unavailable.');
      return;
    }

    const { totalPages: currentTotalPages } = pagingRef.current;
    if (primary.type !== 'items' && currentTotalPages !== null && page > currentTotalPages) {
      setPage(currentTotalPages);
      return;
    }

    /* A reload after a write keeps the old rows on screen until the new ones arrive. */
    const isReload = loadedVersionRef.current !== dataVersion;
    loadedVersionRef.current = dataVersion;
    const controller = new AbortController();
    if (!isReload) {
      setIsLoading(true);
    }
    setError('');

    const loadRows = async () => {
      try {
        const primaryRows =
          primary.type === 'items'
            ? staticRows.slice(0, pageSize).map((item) => item.values)
            : toODataRows(
                await readData({
                  dataSource: readSourceQuery(
                    primary,
                    queryFields,
                    globals,
                    queryInputs,
                    configuredFilters,
                    selectedOrderBy
                  ),
                  options: { top: pageSize, skip: (page - 1) * pageSize },
                  signal: controller.signal,
                })
              );
        let composed: FabrikRenderedListRow[] = primaryRows.map((values, index) => ({
          values: { [primary.id]: values },
          ...(primary.type === 'items' ? { key: staticRows[index].id } : {}),
        }));
        const sourcesById = new Map(dataSources.map((source) => [source.id, source]));
        for (const join of component.joins ?? []) {
          const source = sourcesById.get(join.dataSourceId);
          if (!source) throw new Error(`Join datasource ${join.dataSourceId} is unavailable.`);
          if (source.type === 'odata' && !hasRequiredODataFieldType(join.on, join.fieldType)) {
            throw new Error('Date/time globals joins require the target OData field type.');
          }
          const joinValues = [
            ...new Set(
              composed
                .map((row) => valueForReference(row.values, join.on, globals))
                .filter((value) => value !== undefined && value !== null)
            ),
          ];
          /* A join reads matches by a field filter, which Workday reads cannot do. */
          if (source.type === 'workday') {
            throw new Error('Workday sources cannot be joined.');
          }
          const matches =
            source.type === 'items'
              ? source.items.map((item) => item.values)
              : joinValues.length
              ? (
                  await Promise.all(
                    buildJoinFilterBatches(
                      source,
                      queryFields,
                      globals,
                      queryInputs,
                      join.field,
                      join.fieldType,
                      joinValues,
                      pageSize
                    ).map(async (filter) =>
                      toODataRows(
                        await readData({
                          dataSource: querySource(source, queryFields, globals, queryInputs, [
                            filter,
                          ]),
                          options: { top: pageSize, skip: 0 },
                          signal: controller.signal,
                        })
                      )
                    )
                  )
                ).flat()
              : [];
          composed = composeRows(composed, source, join, matches, globals);
        }
        if (controller.signal.aborted) return;
        setRows(composed);
        setIsLoading(false);
      } catch (reason: unknown) {
        if (controller.signal.aborted) return;
        setRows([]);
        setIsLoading(false);
        setError(reason instanceof Error ? reason.message : 'Unable to load datasource rows.');
      }
    };

    void loadRows();
    return () => controller.abort();
  }, [
    component.joins,
    component.primaryDataSourceId,
    configuredFilters,
    selectedOrderBy,
    staticRows,
    dataSources,
    dataVersion,
    page,
    pageSize,
    queryFields,
    queryInputs,
    queryKey,
    globals,
    readData,
    retryCount,
    setPage,
  ]);

  const truncatedStaticItems = staticRows.length > pageSize;
  const hasActions = Boolean(component.target);
  const emptyStateColSpan = Math.max(component.columns.length + (hasActions ? 1 : 0), 1);
  const target = component.target;
  const screenTarget = target?.type === 'screen' ? target : undefined;
  const fixedLinkUrl =
    target?.type === 'link' && typeof target.url === 'string' ? safeExternalUrl(target.url) : null;
  const serverLink = target?.type === 'link' && typeof target.url !== 'string';
  const componentState = useComponentState();
  const [linkError, setLinkError] = useState<string | null>(null);
  const { blockedActionIds } = useComponentStateStore();
  /* Header actions the viewer may not run are left out, so an empty toolbar is not drawn. */
  const headerActions = (component.headerActions ?? []).filter(
    ({ target: actionTarget }) => !blockedActionIds.has(actionTarget.actionId)
  );
  const filters = component.filters ?? [];
  const sortChoices = component.sortChoices ?? [];
  const resultTotal = primarySource?.type === 'items' ? staticRows.length : knownCount;
  const countLabel =
    resultTotal === null
      ? countLoaded
        ? 'Count unavailable'
        : 'Loading count…'
      : resultTotal.toLocaleString();
  /* The header chip carries the total; the footer only speaks up when this page shows part of it. */
  const showsPartialCount = Boolean(
    component.resultCount &&
      !isLoading &&
      !error &&
      resultTotal !== null &&
      rows.length < resultTotal
  );
  const smallButtonLook = appButtonLook({ size: 'sm' });
  const hasFilters = Object.keys(filterValues).length > 0;
  /* The hint names any sort or filter in use; reset only shows when it would change something. */
  const usesSortOrFilter = Boolean(activeSort) || hasFilters;
  const canResetSortAndFilters = selectedSortIndex > 0 || hasFilters;
  /** Goes back to the first sort choice and clears every filter. */
  const resetSortAndFilters = () => {
    setSortState({ componentId: component.id, index: 0 });
    clearFilterValues(component.id);
  };
  const hasToolbar = Boolean(
    component.title ||
      headerActions.length ||
      sortChoices.length ||
      filters.length ||
      component.resultCount
  );

  return (
    <section>
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        {hasToolbar && (
          <div className="flex min-h-14 flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-2.5">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              {component.title && (
                <h3
                  data-fabrik-selection-path={selectionPath ? `${selectionPath}/title` : undefined}
                  className="truncate type-heading font-semibold text-card-foreground"
                >
                  {component.title}
                </h3>
              )}
              {component.resultCount && (
                <span
                  data-fabrik-selection-path={
                    selectionPath ? `${selectionPath}/runtime/resultCount` : undefined
                  }
                  data-fabrik-selection-definition-path={
                    selectionPath ? `${selectionPath}/resultCount` : undefined
                  }
                  className="rounded-full bg-muted px-2 py-0.5 type-caption font-medium text-muted-foreground tabular-nums"
                  aria-live="polite"
                  aria-label={resultTotal === null ? countLabel : `${countLabel} results`}
                >
                  {resultTotal === null && !countLoaded ? (
                    <Skeleton className="h-3 w-6 bg-foreground/10" aria-hidden="true" />
                  ) : (
                    countLabel
                  )}
                </span>
              )}
            </div>
            <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
              {sortChoices.length > 0 && (
                <Field orientation="horizontal" className="w-auto shrink-0 items-center gap-2">
                  <FieldLabel>Sort by</FieldLabel>
                  <Select
                    items={sortChoices.map((choice, index) => ({
                      label: choice.label,
                      value: String(index),
                    }))}
                    value={String(selectedSortIndex)}
                    onValueChange={(value) =>
                      setSortState({ componentId: component.id, index: Number(value ?? 0) })
                    }
                  >
                    <SelectTrigger
                      data-fabrik-selection-path={
                        selectionPath
                          ? `${selectionPath}/sortChoices/${selectedSortIndex}`
                          : undefined
                      }
                      aria-label="Sort list"
                      className="h-app-control w-48 bg-card dark:bg-card"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {sortChoices.map((choice, index) => (
                          <SelectItem
                            key={`${choice.id}-${index}`}
                            value={String(index)}
                            data-fabrik-selection-path={
                              selectionPath ? `${selectionPath}/sortChoices/${index}` : undefined
                            }
                          >
                            {choice.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </Field>
              )}
              {filters.length > 0 && (
                <Popover open={filterOpen} onOpenChange={setFilterOpen}>
                  <PopoverTrigger
                    render={
                      <Button
                        type="button"
                        variant="outline"
                        size="icon-lg"
                        data-fabrik-selection-path={
                          selectionPath ? `${selectionPath}/filters` : undefined
                        }
                        className="bg-card dark:bg-card"
                        aria-label="Filters"
                      >
                        <Filter aria-hidden="true" />
                      </Button>
                    }
                  />
                  <PopoverContent align="end" className="w-72">
                    <PopoverTitle className="sr-only">Filters</PopoverTitle>
                    {filters.map((filter: FabrikListFilter, filterIndex) => {
                      const filterKey = filterKeyOf(filter, filterIndex);
                      const options = [
                        { label: 'All', value: 'all' },
                        ...filter.options.map((option, index) => ({
                          label: option.label,
                          value: String(index),
                        })),
                      ];
                      const selected = filter.options.findIndex(
                        (option) => option.value === filterValues[filterKey]
                      );
                      return (
                        <Field
                          key={filterKey}
                          data-fabrik-selection-path={
                            selectionPath ? `${selectionPath}/filters/${filterIndex}` : undefined
                          }
                          orientation="horizontal"
                          className="items-center justify-between gap-3"
                        >
                          <FieldLabel
                            data-fabrik-selection-path={
                              selectionPath
                                ? `${selectionPath}/filters/${filterIndex}/label`
                                : undefined
                            }
                            className="min-w-0 truncate"
                          >
                            {filter.label}
                          </FieldLabel>
                          <Select
                            items={options}
                            value={selected < 0 ? 'all' : String(selected)}
                            onValueChange={(selectedValue) => {
                              const option =
                                selectedValue === null || selectedValue === 'all'
                                  ? undefined
                                  : filter.options[Number(selectedValue)];
                              setFilterValue({
                                listId: component.id,
                                filterKey,
                                value: option?.value,
                              });
                            }}
                          >
                            <SelectTrigger
                              data-fabrik-selection-path={
                                selectionPath
                                  ? `${selectionPath}/filters/${filterIndex}`
                                  : undefined
                              }
                              aria-label={`Filter by ${filter.label}`}
                              className="h-app-control w-40 shrink-0 bg-card dark:bg-card"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                <SelectItem value="all">All</SelectItem>
                                {filter.options.map((option, index) => (
                                  <SelectItem
                                    key={`${filter.id}-${index}`}
                                    value={String(index)}
                                    data-fabrik-selection-path={
                                      selectionPath
                                        ? `${selectionPath}/filters/${filterIndex}/options/${index}`
                                        : undefined
                                    }
                                  >
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </Field>
                      );
                    })}
                  </PopoverContent>
                </Popover>
              )}
              {headerActions.map(({ label, target: actionTarget, style }, index) => (
                <ActionButton
                  key={`${label}-${index}`}
                  selectionPath={
                    selectionPath ? `${selectionPath}/headerActions/${index}` : undefined
                  }
                  actionId={actionTarget.actionId}
                  label={label}
                  variant={style?.variant}
                  size="sm"
                  fields={fields}
                  inputs={inputs}
                  screenId={screenId}
                  screenComponents={screenComponents}
                  alignEnd
                />
              ))}
            </div>
          </div>
        )}
        {linkError && <InlineAlert className="mx-4 my-2">{linkError}</InlineAlert>}
        <Table>
          {/* A light grey strip with darker labels, so the header reads as column names, not a row. */}
          <TableHeader className="bg-background">
            <TableRow className="border-b-border hover:bg-transparent">
              {component.columns.map((column, columnIndex) => (
                <TableHead
                  data-fabrik-selection-path={
                    selectionPath ? `${selectionPath}/columns/${columnIndex}/label` : undefined
                  }
                  data-fabrik-selection-definition-path={
                    selectionPath ? `${selectionPath}/columns/${columnIndex}` : undefined
                  }
                  className={cn(
                    'h-9 px-3 type-caption font-semibold text-foreground/70 first:pl-4 last:pr-4',
                    isNumberColumn(column) && 'text-right'
                  )}
                  key={column.name}
                >
                  {column.label}
                </TableHead>
              ))}
              {hasActions && (
                <TableHead className="w-10 px-2 pr-4">
                  <span className="sr-only">Actions</span>
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <LoadingRows columnCount={emptyStateColSpan} />
            ) : error ? (
              <TableRow className="hover:bg-transparent">
                <TableCell className="p-4 whitespace-normal" colSpan={emptyStateColSpan}>
                  <InlineAlert variant="box">
                    <p className="font-medium">This list could not load.</p>
                    <p className="mt-0.5 type-small text-muted-foreground">
                      {error}
                      {usesSortOrFilter && ' The system may not support the chosen sort or filter.'}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2 text-foreground">
                      <Button
                        type="button"
                        {...smallButtonLook}
                        onClick={() => setRetryCount((count) => count + 1)}
                      >
                        Try again
                      </Button>
                      {canResetSortAndFilters && (
                        <Button type="button" {...smallButtonLook} onClick={resetSortAndFilters}>
                          Reset sort and filters
                        </Button>
                      )}
                    </div>
                  </InlineAlert>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  className="py-8 text-center text-sm text-muted-foreground"
                  colSpan={emptyStateColSpan}
                >
                  No rows found.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => {
                const key = rowKey(component.id, row, component.primaryDataSourceId, index);
                const itemLabel = rowLabel(component.columns, row.values, key, globals);
                const clickable = Boolean(
                  screenTarget || fixedLinkUrl || (serverLink && componentState)
                );
                const openRow = () => {
                  if (screenTarget) onOpenTarget(screenTarget, row.values, key);
                  else if (fixedLinkUrl) window.open(fixedLinkUrl, '_blank', 'noopener,noreferrer');
                  else if (serverLink && componentState) {
                    setLinkError(null);
                    openServerLink({
                      resolve: () =>
                        componentState.resolveLink({
                          screenId,
                          componentId: component.id,
                          fields: row.values,
                        }),
                      onError: setLinkError,
                    });
                  }
                };

                const rowPath = selectionPath
                  ? `${selectionPath}/rows/${jsonPointerSegment(key)}`
                  : undefined;
                return (
                  <TableRow
                    data-fabrik-selection-path={rowPath}
                    data-state={selectedItemId === key ? 'selected' : undefined}
                    key={key}
                    tabIndex={clickable ? 0 : undefined}
                    aria-label={
                      clickable
                        ? screenTarget
                          ? `Open ${itemLabel}`
                          : `Open ${itemLabel} in a new tab`
                        : undefined
                    }
                    className={
                      clickable
                        ? 'cursor-pointer focus-visible:bg-muted/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring'
                        : undefined
                    }
                    onClick={clickable ? openRow : undefined}
                    onKeyDown={
                      clickable
                        ? (event) => {
                            if (event.target !== event.currentTarget) return;
                            if (event.key === 'Enter' || (screenTarget && event.key === ' ')) {
                              event.preventDefault();
                              event.currentTarget.click();
                            }
                          }
                        : undefined
                    }
                  >
                    {renderCells(
                      component.columns,
                      row.values,
                      globals,
                      labelOf,
                      selectionPath,
                      rowPath
                    )}
                    {hasActions && (
                      <TableCell className="w-10 px-2 pr-4 text-right">
                        {screenTarget ? (
                          <ChevronRight className="ml-auto h-3 w-3" aria-hidden="true" />
                        ) : fixedLinkUrl || (serverLink && componentState) ? (
                          <ExternalLink className="ml-auto h-3 w-3" aria-hidden="true" />
                        ) : null}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        {(showsPartialCount || (primarySource && primarySource.type !== 'items')) && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-2.5 type-small text-muted-foreground">
            {showsPartialCount && (
              <span className="tabular-nums">
                Showing {rows.length.toLocaleString()} of {resultTotal?.toLocaleString()}
              </span>
            )}
            {primarySource && primarySource.type !== 'items' && (
              <nav className="ml-auto flex items-center justify-end gap-2" aria-label="List pages">
                <Button
                  type="button"
                  variant="outline"
                  disabled={page <= 1 || isLoading}
                  onClick={() => setPage(Math.max(1, page - 1))}
                >
                  <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                  Previous
                </Button>
                <Input
                  id={`list-page-${component.id}`}
                  aria-label="Page number"
                  className="h-8 w-16 px-2 text-center"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={pageInput}
                  disabled={totalPages === null || isLoading}
                  onChange={(event) => setPageInput(event.currentTarget.value)}
                  onBlur={commitPageInput}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      commitPageInput();
                    }
                  }}
                />
                {totalPages === null ? (
                  <span className="min-w-28 text-center text-sm text-muted-foreground">
                    {rows.length} rows loaded
                  </span>
                ) : (
                  <span className="text-sm text-muted-foreground">of {totalPages}</span>
                )}
                <Button
                  type="button"
                  variant="outline"
                  disabled={
                    isLoading || (totalPages === null ? rows.length < pageSize : page >= totalPages)
                  }
                  onClick={() => setPage(page + 1)}
                >
                  Next
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </Button>
              </nav>
            )}
          </div>
        )}
      </div>
      {truncatedStaticItems && !component.resultCount && (
        <p className="mt-2 text-right text-xs text-muted-foreground">
          Showing the first {pageSize} of {staticRows.length} rows.
        </p>
      )}
    </section>
  );
}
